"use client";

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { ShoppingBasket, ArrowLeft, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useCart } from "@/contexts/CartContext";
import { Label } from "@/components/ui/label";
import api from "@/api";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";

import {
  allItems,
  type CatalogItem,
  getImageSources,
} from "@/data/CatalogData";

/* -------------------------------------------------------------
   <picture> with WebP → fallback
   ------------------------------------------------------------- */

type ItemPictureProps = {
  basename: string;
  alt: string;
  className?: string;
  imgClassName?: string;
  category?: string;
  loading?: "lazy" | "eager";
};

function ItemPicture({
  basename,
  alt,
  className,
  imgClassName,
  category,
  loading = "lazy",
}: ItemPictureProps) {
  const { webp, fallback } = useMemo(
    () => getImageSources(basename, category),
    [basename, category]
  );

  return (
    <picture className={className}>
      {webp && <source srcSet={webp} type="image/webp" />}
      <img
        src={fallback}
        alt={alt}
        className={imgClassName}
        loading={loading}
        decoding="async"
      />
    </picture>
  );
}

/* -------------------------------------------------------------
   ITEM DETAILS PAGE
   ------------------------------------------------------------- */

export default function ItemDetails() {
  const { t } = useLanguage();
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem } = useCart();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const normalizeImages = (value?: string | string[]) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  };

  // Translation cache
  const translationCache = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    translationCache.current = new Map();
  }, [t]);

  const getTranslatedField = useCallback(
    (key?: string | null): string => {
      if (!key || typeof key !== "string") return "";

      const cached = translationCache.current.get(key);
      if (cached) return cached;

      const keys = key.split(".");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let value: any = t;
      for (const k of keys) {
        if (value && typeof value === "object" && k in value) {
          value = value[k];
        } else {
          translationCache.current.set(key, key);
          return key;
        }
      }
      const result = typeof value === "string" ? value : key;
      translationCache.current.set(key, result);
      return result;
    },
    [t]
  );

  // Find item by ID - memoized
  const item = useMemo(
    () => allItems.find((i) => i.id === Number(itemId)),
    [itemId]
  );

  // Get current locale from URL - memoized
  const currentLocale = useMemo(() => {
    if (!location.pathname) return "en";
    const pathParts = location.pathname.split("/").filter(Boolean);
    return pathParts[0] || "en";
  }, [location.pathname]);

  // State declarations must come before useMemos that depend on them
  const [qty, setQty] = useState<number | "">(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [selectedSizeKey, setSelectedSizeKey] = useState<string | null>(
    item?.sizes?.[0] ?? null
  );
  const [selectedColorKey, setSelectedColorKey] = useState<string | null>(
    item?.colors?.[0] ?? null
  );
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [variantPrice, setVariantPrice] = useState<number | null>(null);

  // Memoize derived values
  const name = useMemo(
    () => (item ? getTranslatedField(item.nameKey) : ""),
    [item, getTranslatedField]
  );
  
  // Get description - use size-specific description if available, else fall back to generic description
  const desc = useMemo(() => {
    if (!item) return "";
    
    // If a size is selected and there's a size-specific description mapping
    if (selectedSizeKey && item.sizesDescription?.[selectedSizeKey]) {
      return getTranslatedField(item.sizesDescription[selectedSizeKey]);
    }
    
    // Fall back to generic description
    return getTranslatedField(item.descriptionKey);
  }, [item, selectedSizeKey, getTranslatedField]);

  const minQty = useMemo(() => 1, []);

  // Restore image handling - get basenames from item
  const basenames = useMemo(
    () =>
      item?.imageBases?.length
        ? item.imageBases
        : item?.imageBase
        ? [item.imageBase]
        : [],
    [item?.imageBases, item?.imageBase]
  );

  const fallbackPrice = useMemo(
    () => (selectedSizeKey && item?.sizePrices?.[selectedSizeKey]) || item?.price || 0,
    [item, selectedSizeKey]
  );

  const effectivePrice = variantPrice != null ? variantPrice : fallbackPrice;

  useEffect(() => {
    if (!item) {
      setVariantPrice(null);
      return;
    }

    let isActive = true;
    const loadVariantPrice = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedColorKey) params.append("color", selectedColorKey);
        if (selectedSizeKey) params.append("size", selectedSizeKey);

        const stockRes = await api.get(`/api/stock/check/${item.id}?${params.toString()}`);
        if (!isActive) return;

        const price = stockRes.data?.price;
        setVariantPrice(price != null ? Number(price) : null);
      } catch (err) {
        if (isActive) {
          setVariantPrice(null);
        }
      }
    };

    loadVariantPrice();

    return () => {
      isActive = false;
    };
  }, [item, selectedColorKey, selectedSizeKey]);

  // Swipe handling for image gallery
  const touchStartX = useRef<number | null>(null);
  const isSwiping = useRef(false);
  const lastDeltaX = useRef(0);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      touchStartX.current = e.touches[0].clientX;
      isSwiping.current = false;
      lastDeltaX.current = 0;
    },
    []
  );

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const deltaX = e.touches[0].clientX - touchStartX.current;
    lastDeltaX.current = deltaX;
    if (Math.abs(deltaX) > 15) {
      isSwiping.current = true;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping.current) {
      touchStartX.current = null;
      lastDeltaX.current = 0;
      return;
    }

    const dx = lastDeltaX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) {
        setActiveImageIndex((prev) =>
          prev === basenames.length - 1 ? 0 : prev + 1
        );
      } else {
        setActiveImageIndex((prev) =>
          prev === 0 ? basenames.length - 1 : prev - 1
        );
      }
    }

    touchStartX.current = null;
    lastDeltaX.current = 0;
    isSwiping.current = false;
  }, [basenames.length]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    setQty(raw === "" ? "" : Number(raw));
  }, []);

  const handleBlur = useCallback(() => {
    if (!qty || qty < minQty) {
      setQty(minQty);
    }
  }, [qty, minQty]);

  const increment = useCallback(
    () => setQty((prev) => (prev === "" ? minQty + minQty : prev + minQty)),
    [minQty]
  );

  const decrement = useCallback(
    () =>
      setQty((prev) => {
        if (prev === "") return minQty;
        return Math.max(minQty, prev - minQty);
      }),
    [minQty]
  );

  const handleAdd = useCallback(async () => {
    if (!item) return;

    const finalQty = qty || minQty;

    // Variant labels (translated) - size and color
    const details: string[] = [];
    if (selectedSizeKey) details.push(getTranslatedField(selectedSizeKey));
    if (selectedColorKey) details.push(getTranslatedField(selectedColorKey));

    let displayName = name;
    if (details.length) {
      displayName = `${name} (${details.join(", ")})`;
    }

    // Get main image from basenames
    let mainImage = "";
    if (basenames.length > 0) {
      const { fallback } = getImageSources(basenames[0], item.category);
      mainImage = fallback || "";
    }

    // ============= CHECK STOCK BEFORE ADDING =============
    try {
      const params = new URLSearchParams();
      if (selectedColorKey) params.append("color", selectedColorKey);
      if (selectedSizeKey) params.append("size", selectedSizeKey);

      const stockRes = await api.get(`/api/stock/check/${item.id}?${params.toString()}`);
      
      if (!stockRes.data.available) {
        toast({
          title: "Out of Stock",
          description: stockRes.data.message || "This item is not available right now",
          variant: "destructive",
        });
        return;
      }

      if (stockRes.data.quantity < finalQty) {
        toast({
          title: "Insufficient Stock",
          description: `Only ${stockRes.data.quantity} available in stock. You requested ${finalQty}.`,
          variant: "destructive",
        });
        return;
      }
    } catch (err) {
      console.error("Stock check failed:", err);
      // Continue anyway if stock check fails
    }

    // Create unique variant ID: base_id-size-color
    const variantId = [
      item.id,
      selectedSizeKey || "nosize",
      selectedColorKey || "nocolor"
    ].join("-");

    addItem(
      {
        id: variantId,
        name: displayName,
        description: desc,
        price: effectivePrice,
        image: mainImage,
      },
      finalQty
    );

    // Show success toast notification
    toast({
      title: t.basket?.added_to_basket || "Item added to basket",
      variant: "default",
    });

    // Navigate back with preserved catalog state
    const params = new URLSearchParams();
    const state = location.state as { category?: string; search?: string; page?: number } | null;
    if (state?.category) {
      if (state.category !== 'all') params.set('category', state.category);
      if (state.search) params.set('search', state.search);
      if (state.page && state.page > 1) params.set('page', state.page.toString());
      navigate(`/${currentLocale}/catalog?${params.toString()}`, { state: {} });
    } else {
      navigate(`/${currentLocale}/catalog`);
    }
  }, [
    item,
    qty,
    minQty,
    selectedSizeKey,
    selectedColorKey,
    name,
    desc,    basenames,    currentLocale,
    navigate,
    addItem,
    location.state,
    t.basket?.added_to_basket,
    toast,
    getTranslatedField,
    effectivePrice,
  ]);

  const handleBack = useCallback(() => {
    // Try to get catalog state from location, otherwise navigate to catalog
    const params = new URLSearchParams();
    const state = location.state as { category?: string; search?: string; page?: number } | null;
    if (state?.category) {
      if (state.category !== 'all') params.set('category', state.category);
      if (state.search) params.set('search', state.search);
      if (state.page && state.page > 1) params.set('page', state.page.toString());
      navigate(`/${currentLocale}/catalog?${params.toString()}`, { state: {} });
    } else {
      navigate(`/${currentLocale}/catalog`);
    }
  }, [currentLocale, navigate, location.state]);

  useEffect(() => {
    if (!item) {
      navigate(`/${currentLocale}/catalog`);
      return;
    }

    setQty(minQty);
    setActiveImageIndex(0);
    setSelectedSizeKey(item.sizes?.[0] ?? null);
    setSelectedColorKey(item.colors?.[0] ?? null);
  }, [itemId, item, minQty, navigate, currentLocale]);

  // Update image when selected size changes (if sizeImages mapping exists)
  useEffect(() => {
    if (!item?.sizeImages || !selectedSizeKey || basenames.length === 0) return;

    const sizeImageData = item.sizeImages[selectedSizeKey];
    if (!sizeImageData) return;

    // Normalize to array
    const sizeImages = Array.isArray(sizeImageData) ? sizeImageData : [sizeImageData];

    // Find which basename matches this size
    const imageIndex = basenames.findIndex((b) => sizeImages.includes(b));

    if (imageIndex !== -1) {
      setActiveImageIndex(imageIndex);
    }
  }, [selectedSizeKey, item?.sizeImages, basenames]);

  // Update selected size when image changes (via arrows, dots, or swipe)
  useEffect(() => {
    if (!item?.sizeImages || basenames.length === 0) return;

    const currentImageBasename = basenames[activeImageIndex];
    if (!currentImageBasename) return;

    // Find which size has this image
    for (const [sizeKey, sizeImageData] of Object.entries(item.sizeImages)) {
      const sizeImages = Array.isArray(sizeImageData) ? sizeImageData : [sizeImageData];
      if (sizeImages.includes(currentImageBasename)) {
        setSelectedSizeKey(sizeKey);
        return;
      }
    }
  }, [activeImageIndex, item?.sizeImages, basenames]);

  // Update image when selected color changes (if colorImages mapping exists)
  useEffect(() => {
    if (!item?.colorImages || !selectedColorKey || basenames.length === 0) return;

    const colorImageData = item.colorImages[selectedColorKey];
    if (!colorImageData) return;

    // Normalize to array
    const colorImages = Array.isArray(colorImageData) ? colorImageData : [colorImageData];

    // Find which basename matches this color
    const imageIndex = basenames.findIndex((b) => colorImages.includes(b));

    if (imageIndex !== -1) {
      setActiveImageIndex(imageIndex);
    }
  }, [selectedColorKey, item?.colorImages, basenames]);

  // Update selected color when image changes (via arrows, dots, or swipe)
  useEffect(() => {
    if (!item?.colorImages || basenames.length === 0) return;

    const currentImageBasename = basenames[activeImageIndex];
    if (!currentImageBasename) return;

    // Find which color has this image
    for (const [colorKey, colorImageData] of Object.entries(item.colorImages)) {
      const colorImages = Array.isArray(colorImageData) ? colorImageData : [colorImageData];
      if (colorImages.includes(currentImageBasename)) {
        setSelectedColorKey(colorKey);
        return;
      }
    }
  }, [activeImageIndex, item?.colorImages, basenames]);

  if (!item) {
    return null;
  }

  return (
    <Layout>
      <SEO
        title={`${name} | ${t.nav?.catalog || "Catalog"}`}
        description={desc}
        path={`/catalog/${itemId}`}
      />

      <section className="container mx-auto px-4 py-8">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-6 flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.catalog?.backToCatalog || "Back to Catalog"}
        </Button>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Left: Image Gallery */}
          <div className="space-y-4">
            <div
              className="relative h-96 md:h-[500px] w-full overflow-hidden rounded-lg bg-gray-50 border-2 border-primary cursor-pointer hover:opacity-90 transition-opacity"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onClick={() => setIsFullscreenOpen(true)}
            >
              <div className="relative w-full h-full">
                {basenames.map((basename, idx) => {
                  // Only render current image for performance
                  if (idx !== activeImageIndex) return null;

                  return (
                    <div
                      key={idx}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <ItemPicture
                        basename={basename}
                        alt={name}
                        category={item.category}
                        className="h-full w-full flex items-center justify-center"
                        imgClassName="h-full w-full object-cover"
                        loading="eager"
                      />
                    </div>
                  );
                })}
              </div>

              {basenames.length > 1 && (
                <>
                  {/* Left Arrow */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveImageIndex((prev) =>
                        prev === 0 ? basenames.length - 1 : prev - 1
                      );
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-primary rounded-full p-2 shadow-md z-10"
                    aria-label="Previous image"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  {/* Right Arrow */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveImageIndex((prev) =>
                        prev === basenames.length - 1 ? 0 : prev + 1
                      );
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-primary rounded-full p-2 shadow-md z-10"
                    aria-label="Next image"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>

                  {/* Dots */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {basenames.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setActiveImageIndex(idx)}
                        className={`h-2 w-2 rounded-full ${
                          idx === activeImageIndex
                            ? "bg-primary scale-125"
                            : "bg-primary/30 hover:bg-primary/50"
                        }`}
                        aria-label={`Show image ${idx + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail gallery for desktop */}
            {basenames.length > 1 && (
              <div className="hidden md:flex gap-3 overflow-x-auto overflow-y-hidden scrollbar-hide whitespace-nowrap">
                {basenames.map((basename, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveImageIndex(idx)}
                    className={`flex-shrink-0 w-24 h-24 rounded border-2 overflow-hidden transition-all ${
                      idx === activeImageIndex
                        ? "border-primary"
                        : "border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    <ItemPicture
                      basename={basename}
                      alt={`${name} thumbnail ${idx + 1}`}
                      category={item.category}
                      imgClassName="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Product Details */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold mb-4 flex items-center gap-2">
                {name}
              </h1>
              {desc && (
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    {t.catalog?.itemDetail?.description || 'Description'}
                  </h2>
                  <p className="text-base text-gray-600 leading-relaxed">
                    {desc}
                  </p>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <span className="text-3xl font-bold text-primary">
                UZS{" "}
                {effectivePrice.toFixed(2)}{" "}
                <span className="text-lg font-normal text-muted-foreground">
                  {t.catalog?.itemDetail?.each || 'each'}
                </span>
              </span>
            </div>

            {/* SIZES (translated) */}
            {item.sizes && item.sizes.length > 0 && (
              <div className="space-y-2">
                <Label className="text-base font-semibold">{t.catalog?.itemDetail?.size || 'Size'}</Label>
                <div className="flex flex-wrap gap-2">
                  {item.sizes.map((sizeKey) => {
                    const label = getTranslatedField(sizeKey);
                    return (
                      <button
                        key={sizeKey}
                        type="button"
                        onClick={() => {
                          setSelectedSizeKey(sizeKey);
                        }}
                        className={`px-4 py-2 text-sm rounded-md border transition ${
                          selectedSizeKey === sizeKey
                            ? "bg-primary text-white border-primary"
                            : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* COLORS (translated) */}
            {item.colors && item.colors.length > 0 && (
              <div className="space-y-2">
                <Label className="text-base font-semibold">{t.catalog?.itemDetail?.color || 'Color'}</Label>
                <div className="flex flex-wrap gap-2">
                  {item.colors.map((colorKey) => {
                    const label = getTranslatedField(colorKey);
                    return (
                      <button
                        key={colorKey}
                        type="button"
                        onClick={() => {
                          setSelectedColorKey(colorKey);
                        }}
                        className={`px-4 py-2 text-sm rounded-md border transition ${
                          selectedColorKey === colorKey
                            ? "bg-primary text-white border-primary"
                            : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* QUANTITY */}
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="qty" className="text-base font-semibold">
                {t.catalog?.itemDetail?.quantity || 'Quantity'}
              </Label>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-12 w-12"
                  onClick={decrement}
                >
                  −
                </Button>

                <Input
                  id="qty"
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={qty}
                  placeholder={minQty.toString()}
                  className="w-32 text-center text-lg"
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-12 w-12"
                  onClick={increment}
                >
                  +
                </Button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                {t.catalog?.itemDetail?.cancel || 'Cancel'}
              </Button>
              <Button onClick={handleAdd} className="flex-1" size="lg">
                <ShoppingBasket className="h-5 w-5 mr-2" />
                {(t.catalog?.itemDetail?.addItem || 'Add {qty} {itemLabel}').replace('{qty}', (qty || minQty).toString()).replace('{itemLabel}', (qty === 1 ? t.basket?.item : t.basket?.items) || (qty === 1 ? 'item' : 'items'))}
              </Button>
            </div>
          </div>
        </div>

        {/* Fullscreen Photo Modal */}
        <Dialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen}>
          <DialogContent className="max-w-full max-h-[100vh] w-screen h-screen p-0 bg-black/95 border-0 rounded-0 flex items-center justify-center">
            <button
              onClick={() => setIsFullscreenOpen(false)}
              className="absolute top-4 right-4 z-50 text-white hover:text-gray-300 transition-colors"
              aria-label="Close fullscreen"
            >
              <X className="h-8 w-8" />
            </button>

            {/* Main Fullscreen Image */}
            <div className="relative w-full h-full flex items-center justify-center">
              {basenames.map((basename, idx) => {
                if (idx !== activeImageIndex) return null;

                return (
                  <div
                    key={idx}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <ItemPicture
                      basename={basename}
                      alt={name}
                      category={item?.category}
                      className="h-full w-full flex items-center justify-center"
                      imgClassName="max-h-full max-w-full object-contain"
                      loading="eager"
                    />
                  </div>
                );
              })}

              {basenames.length > 1 && (
                <>
                  {/* Left Arrow */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveImageIndex((prev) =>
                        prev === 0 ? basenames.length - 1 : prev - 1
                      );
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
                    aria-label="Previous image"
                  >
                    <svg
                      className="w-10 h-10"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  {/* Right Arrow */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveImageIndex((prev) =>
                        prev === basenames.length - 1 ? 0 : prev + 1
                      );
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
                    aria-label="Next image"
                  >
                    <svg
                      className="w-10 h-10"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>

                  {/* Dots - Bottom Center */}
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {basenames.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setActiveImageIndex(idx)}
                        className={`h-2 w-2 rounded-full transition-colors ${
                          idx === activeImageIndex
                            ? "bg-white"
                            : "bg-white/30 hover:bg-white/50"
                        }`}
                        aria-label={`Show image ${idx + 1}`}
                      />
                    ))}
                  </div>

                  {/* Image Counter - Bottom Left */}
                  <div className="absolute bottom-8 left-8 text-white text-sm font-medium z-10">
                    {activeImageIndex + 1} / {basenames.length}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </section>
    </Layout>
  );
}
